import React, { useState, useContext } from 'react';
import styled from '@emotion/styled';
import OutsideClickHandler from 'react-outside-click-handler';
import { theme } from '@styles';
import { WHITE, GRANNY_SMITH, OUTER_SPACE } from '@constants';
import { useI18n } from '../../context/I18nContext';
import { NavigationThemeContext } from '../../context/NavigationThemeContext';
import {
  getPathWithoutLocale,
  getLocalizedPath,
  getSearchWithoutCacheBustParam,
  LOCALE_SWITCH_CACHE_BUST_PARAM,
} from '@utils/websiteUrlUtils';
import { getSupportedLocales } from '@config/i18n';
import { getLocaleDisplayName } from '../../constants/languages';
import GlobeIcon from '@assets/icons/GlobeIcon';
import type { LocaleCode } from '@config/i18n';
import { isBrowser } from '@utils';

const { breakpoints } = theme;

interface LanguageSelectorProps {
  variant?: 'desktop' | 'header';
  onMenuClose?: () => void;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ variant = 'desktop', onMenuClose }) => {
  const { t, locale } = useI18n();
  const { navigationTheme } = useContext(NavigationThemeContext);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const supportedLocales = getSupportedLocales();

  const handleLocaleSelect = (newLocale: LocaleCode) => {
    if (newLocale === locale) {
      setIsDropdownOpen(false);
      onMenuClose?.();
      return;
    }

    if (isBrowser()) {
      const basePath = getPathWithoutLocale(window.location.pathname);
      const newPath = getLocalizedPath(basePath, newLocale);
      const searchWithoutT = getSearchWithoutCacheBustParam(window.location.search);
      const fullPath = searchWithoutT ? `${newPath}${searchWithoutT}` : newPath;
      const separator = fullPath.includes('?') ? '&' : '?';
      window.location.replace(
        `${fullPath}${separator}${LOCALE_SWITCH_CACHE_BUST_PARAM}=${Date.now()}`
      );
    }

    setIsDropdownOpen(false);
    onMenuClose?.();
  };

  const isHeader = variant === 'header';
  const Container = isHeader ? HeaderContainer : DesktopContainer;

  return (
    <Container>
      <OutsideClickHandler onOutsideClick={() => setIsDropdownOpen(false)}>
        <TriggerButton
          type="button"
          theme={navigationTheme}
          $isHeader={isHeader}
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          aria-label={t('common:nav.language')}
          aria-expanded={isDropdownOpen}
          aria-haspopup="listbox"
        >
          <GlobeIcon
            stroke={isHeader ? OUTER_SPACE : navigationTheme === 'dark' ? WHITE : OUTER_SPACE}
            strokeWidth={2}
          />
        </TriggerButton>
        {isDropdownOpen && (
          <DropdownMenu role="listbox" $alignRight={isHeader}>
            {supportedLocales.map((loc) => (
              <DropdownItem
                key={loc}
                role="option"
                aria-selected={loc === locale}
                onClick={() => handleLocaleSelect(loc)}
                $isActive={loc === locale}
              >
                {getLocaleDisplayName(loc)}
              </DropdownItem>
            ))}
          </DropdownMenu>
        )}
      </OutsideClickHandler>
    </Container>
  );
};

export default LanguageSelector;

const DesktopContainer = styled.div`
  position: relative;
  display: none;

  @media screen and (min-width: ${breakpoints.smallLaptop}) {
    display: block;
  }
`;

const HeaderContainer = styled.div`
  position: relative;
  display: block;
`;

const TriggerButton = styled.button<{
  theme: 'dark' | 'light';
  $isHeader?: boolean;
}>`
  padding: ${({ $isHeader }) => ($isHeader ? '6px 8px' : '12px')};
  background-color: ${({ theme, $isHeader }) =>
    $isHeader ? WHITE : theme === 'dark' ? 'transparent' : WHITE};
  color: ${({ theme, $isHeader }) =>
    $isHeader ? OUTER_SPACE : theme === 'dark' ? WHITE : OUTER_SPACE};
  border: none;
  border-radius: ${({ $isHeader }) => ($isHeader ? '8px' : '12px')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transform: translateY(0px);
  transition: all 250ms ease-in-out;

  &:hover {
    transform: translateY(-3px);
    filter: drop-shadow(0px 4px 10px rgba(0, 0, 0, 0.25));
    opacity: 1;
  }
`;

const DropdownMenu = styled.div<{ $alignRight?: boolean }>`
  position: absolute;
  top: 100%;
  left: ${({ $alignRight }) => ($alignRight ? 'auto' : '0')};
  right: ${({ $alignRight }) => ($alignRight ? '0' : 'auto')};
  margin-top: 8px;
  background-color: ${WHITE};
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  min-width: 200px;
  max-height: 320px;
  overflow-y: auto;
  z-index: 1001;
`;

const DropdownItem = styled.button<{ $isActive?: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  padding: 10px 16px;
  cursor: pointer;
  transition: background-color 0.2s;
  color: ${OUTER_SPACE};
  font-size: 14px;

  &:hover {
    background-color: ${GRANNY_SMITH}20;
  }

  ${({ $isActive }) => $isActive && `font-weight: 600; background-color: ${GRANNY_SMITH}15;`}
`;
